import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgIf, NgFor } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ShipToDetails } from '../../../order/models/ship-to-details';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { WorkflowStepContent } from '../../../shared/components/workflow-steps/workflow-steps.component';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { CallableOrderItem, EcallOffByVolumeStepState, RDD_MODE } from '../../models/ecall-off-model';
import { RddModeService } from '../../services/rdd-mode.service';
import { CallableOrderItemChange, CallableOrderItemComponent } from '../card/callable-order-item.component';
import { EcallOffHeaderForm, EcallOffHeaderComponent } from '../header/ecall-off-header.component';
import {
	InfoEntryCalloffHeaderForm,
	InfoEntryCalloffHeaderComponent
} from '../infoentry/infoentry-calloff-header.component';
import { ECallOffService } from '../../services/ecall-off.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { EcallOffShipToDetails } from '../../../order/models/ecall-off-ship-to-details';

@Component({
	selector: 'copo-by-volume-step-infoentry',
	templateUrl: './by-volume-step-infoentry.component.html',
	styleUrl: './by-volume-step-infoentry.component.css',
	animations: pageTransitions,
	imports: [EcallOffHeaderComponent, NgIf, InfoEntryCalloffHeaderComponent, NgFor, CallableOrderItemComponent]
})
export class ByVolumeStepInfoEntryComponent implements WorkflowStepContent, OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	stepState?: EcallOffByVolumeStepState;
	editDateFrom = false;
	editDateTo = false;
	shipToDetails?: EcallOffShipToDetails[];
	allTransactionalShipToDetails?: ShipToDetails[];
	user?: User;
	minBatchQty: Map<string, number> | undefined;
	sub?: Subscription;
	unloadingRef?: string;
	selectedShipTo?: EcallOffShipToDetails;
	rddModes = RDD_MODE;
	nextSubscription: Subscription;
	isShipToMillValid = false;
	currentRddMode?: string;
	pageContent?: WorkflowStepContent;
	allowChangeOfShipto = false;

	constructor(
		private readonly rddModeService: RddModeService,
		private readonly ecallOffService: ECallOffService,
		private readonly userService: UserService,
		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService
	) {
		this.user = this.userService.user;
		this.sub = this.ecallOffService.unloadingRef
			.subscribe(unloadinRef => {
				this.unloadingRef = unloadinRef;
			});
		this.sub = this.ecallOffService.selectedShipto
			.subscribe(selectedShipto => {
				this.selectedShipTo = selectedShipto;
			});
		this.sub = this.ecallOffService.currentRddMode
			.subscribe(rddMode => {
				this.currentRddMode = rddMode;
			});
		this.nextSubscription = this.ecallOffService.nextClicked.subscribe(() => {
			this.onNext();
		});
	}

	onNext() {
		this.isShipToMillValid = this.stepState && this.currentRddMode === 'EXACT'
			? this.checkMillAndShipToCombination() : true;
	}

	isShipToMillCombinationValid() {
		return this.isShipToMillValid;
	}

	ngOnInit() {
		if (this.stepState?.selectedOrderItems) {
			this.sub = this.ecallOffService.getMinBatchQty(this.stepState.selectedOrderItems).subscribe({
				next: result => {
					this.minBatchQty = result;
					if (this.stepState?.selectedOrderItems && Object.keys(this.minBatchQty).length) {
						for (const data of this.stepState.selectedOrderItems) {
							if (data.supplierOrderNumber && data.supplierOrderItemNumber) {
								data.minQuantity = this.getValueFromMinMap(
									`${ data.supplierOrderNumber }/${ data.supplierOrderItemNumber }`,
									this.minBatchQty
								);
							}
						}
					}
				},
				error: (err: unknown) => {
					console.error('error loading minimum quantity', err);
				}
			});
			this.allowChangeOfShipto = this.stepState.selectedOrderItems
				.some(orderItem => orderItem.allowChangeOfShipto === 1);
		}
		const loginUser = this.user;
		const currentVisibleOrgId = loginUser?.currentVisibility.organizationId;
		if (!currentVisibleOrgId) {
			return;
		}
		this.getShipToValue(currentVisibleOrgId).catch((error: unknown) => {
			console.error(error);
		});
	}

	isStepValidated() {
		const hasCustomerCalloffReference = this.stepState?.customerCalloffReference !== undefined
			&& this.stepState.customerCalloffReference.length > 0;
		const hasRddMode = this.stepState?.rddMode !== undefined && this.stepState.rddMode.length > 0;
		const areDatesValid = this.areDatesValid();
		const areCalledWeightsValid = this.areCalledWeightsValid();
		const hasStillOrderItems =
			this.stepState?.selectedOrderItems !== undefined && this.stepState.selectedOrderItems.length > 0;
		if (!this.stepState?.selectedOrderItems) {
			return false;
		}
		return hasCustomerCalloffReference
			&& hasRddMode
			&& areDatesValid
			&& areCalledWeightsValid
			&& hasStillOrderItems;
	}

	setStepState(state: Record<string, unknown> | undefined) {
		if (state) {
			this.stepState = <EcallOffByVolumeStepState><unknown>state;
			if (!this.stepState.rddMode) {
				this.stepState.rddMode = 'ASAP';
				this.ecallOffService.updateRddMode(this.stepState.rddMode);
			}
			this.updateEditDatesVisibility();
		}
	}

	getUpdatedStepState() {
		return this.stepState;
	}

	getTotalWeight() {
		if (!this.stepState?.selectedOrderItems) {
			return 0;
		}
		let weight = 0;
		for (const item of this.stepState.selectedOrderItems) {
			weight += item.calledWeight ?? 0;
		}
		return weight;
	}

	updateEditDatesVisibility() {
		if (this.stepState?.rddMode) {
			const rddMode = this.rddModeService.getRddMode(this.stepState.rddMode);
			this.editDateFrom = rddMode.canEditDateFrom();
			this.editDateTo = rddMode.canEditDateTo();
		}
	}

	updateDates(orderItem: CallableOrderItem, requestedDate?: Date, fromDate?: Date, toDate?: Date) {
		if (this.stepState?.rddMode) {
			const rddMode = this.rddModeService.getRddMode(this.stepState.rddMode);
			rddMode.updateDates(orderItem, requestedDate, fromDate, toDate);
		}
	}

	canGoBack() {
		return this.stepState?.selectedOrderItems === undefined || this.stepState.selectedOrderItems.length === 0;
	}

	areDatesValid() {
		if (this.stepState?.rddMode
			&& this.stepState.selectedOrderItems
			&& this.stepState.selectedOrderItems.length > 0) {
			const rddMode = this.rddModeService.getRddMode(this.stepState.rddMode);
			for (const orderItem of this.stepState.selectedOrderItems) {
				if (!rddMode.areDatesValid(orderItem)) {
					return false;
				}
			}
			return true;
		}
		return false;
	}

	areCalledWeightsValid() {
		if (this.stepState?.selectedOrderItems && this.stepState.selectedOrderItems.length > 0) {
			for (const orderItem of this.stepState.selectedOrderItems) {
				if (!orderItem.calledWeight
					|| !orderItem.qtyBatchGross
					|| orderItem.calledWeight <= 0
					|| orderItem.calledWeight > orderItem.qtyBatchGross) {
					return false;
				}
			}
			return true;
		}
		return false;
	}

	resetDatesValues() {
		if (this.stepState?.selectedOrderItems) {
			this.stepState.selectedOrderItems = this.stepState.selectedOrderItems.map(orderItem => ({
				...orderItem,
				requestedDeliveryDateFrom: undefined,
				requestedDeliveryDateTo: undefined
			}));
		}
	}

	onInfoEntryHeaderChange(form: EcallOffHeaderForm) {
		const isRddModeDifferent = this.stepState?.rddMode !== form.rddMode;
		this.stepState = {
			...this.stepState,
			copyTo: form.copyTo,
			customerCalloffReference: form.customerCalloffReference,
			rddMode: form.rddMode
		};
		this.ecallOffService.updateRddMode(form.rddMode);
		if (isRddModeDifferent) {
			this.updateEditDatesVisibility();
			this.resetDatesValues();
		}
	}

	onInfoEntryCalloffHeaderApplyAll(form: InfoEntryCalloffHeaderForm) {
		if (!this.stepState?.selectedOrderItems) {
			return;
		}
		this.ecallOffService.updateUnloadingRef(form.unloadingReference);
		this.ecallOffService.updateSelectedShipto(form.shipTo);
		this.stepState.selectedOrderItems = this.stepState.selectedOrderItems.map(orderItem => {
			// eslint-disable-next-line unicorn/prefer-logical-operator-over-ternary
			const originalShipToObj: ShipToDetails = orderItem.originalShipTo
				? orderItem.originalShipTo
				: {
					shipTo: typeof orderItem.shipTo === 'object'
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						? orderItem.shipTo.shipTo ?? '' : orderItem.shipTo ?? '',
					shipToDescription: orderItem.shipToDescription ?? ''
				};
			const newShipTo: ShipToDetails = orderItem.allowChangeOfShipto === 0
				// eslint-disable-next-line @stylistic/no-extra-parens
				? (typeof orderItem.shipTo === 'object' ? orderItem.shipTo : {
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					shipTo: orderItem.shipTo ?? '',
					shipToDescription: orderItem.shipToDescription ?? ''
				})
				: form.shipTo;
			const newOrderItem: CallableOrderItem = {
				...orderItem,
				unloadingReference: form.unloadingReference,
				shipTo: newShipTo,
				originalShipTo: originalShipToObj
			};
			this.updateDates(newOrderItem, form.requestedDate, form.fromDate, form.toDate);
			return newOrderItem;
		});
	}

	onCallableOrderItemChange(form?: CallableOrderItemChange) {
		// only update properties, rendering is fine
		if (!this.stepState?.selectedOrderItems || !form) {
			return;
		}
		const orderItemsToUpdate = this.stepState.selectedOrderItems.filter(orderItem => orderItem.id === form.id);
		let calledWeight: number | undefined;
		if (form.calledVolume) {
			const value = Number.parseFloat(form.calledVolume);
			if (!Number.isNaN(value)) {
				calledWeight = value;
			}
		}
		for (const orderItem of orderItemsToUpdate) {
			orderItem.calledWeight = calledWeight;
			orderItem.comment = form.comment;
			orderItem.unloadingReference = form.unloadingReference;
			orderItem.shipTo = this.shipToDetails && this.shipToDetails.length > 0 ? form.shipTo : orderItem.shipTo;
			this.updateDates(orderItem, undefined, form.requestedDeliveryDateFrom, form.requestedDeliveryDateTo);
		}
	}

	onCallableOrderItemDelete(orderItem?: CallableOrderItem) {
		if (!orderItem?.id) {
			console.warn('Missing id to delete', {
				reference: orderItem?.supplierOrderNumber,
				item: orderItem?.supplierOrderItemNumber
			});
			return;
		}
		if (!this.stepState?.selectedOrderItems) {
			console.warn('No selected order items to delete');
			return;
		}
		this.stepState.selectedOrderItems = this.stepState.selectedOrderItems.filter(
			selected => selected.id !== orderItem.id
		);
	}

	onCallableOrderItemView(orderItem?: CallableOrderItem) {
		if (!orderItem?.supplierOrderNumber || !orderItem.supplierOrderItemNumber) {
			console.warn('Missing supplier order reference or item to view', {
				reference: orderItem?.supplierOrderNumber,
				item: orderItem?.supplierOrderItemNumber
			});
			return;
		}
		const url =
			`/order-tracking/order/card/${ orderItem.supplierOrderNumber }/${ orderItem.supplierOrderItemNumber }`;
		open(url, '_blank');
	}

	async getShipToValue(id: number) {
		try {
			await this.ecallOffService.getShipToDetails(id).then(shipTos => {
				if (this.hasExactModeAvailable(shipTos) && !this.rddModes.includes('EXACT')) {
					this.rddModes.push('EXACT');
				}
				this.allTransactionalShipToDetails = shipTos;
				this.shipToDetails = shipTos.filter(shipto => shipto.allowChangeOfShipto);
				this.ecallOffService.updateSelectedShipTos(this.shipToDetails);
			});
		} catch (err) {
			console.error('Error from http get current shipTo details', err);
		}
	}

	hasExactModeAvailable(shipTos: ShipToDetails[]): boolean {
		return shipTos.some(shipTo => shipTo.millCodes?.some(Boolean)) || false;
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		this.nextSubscription.unsubscribe();
	}

	private getValueFromMinMap(key: string, minMap: Map<string, number>) {
		let minmumBatchQuantity = 0;
		for (const entry of Object.entries(minMap)) {
			if (key === entry[0]) {
				minmumBatchQuantity = <number>entry[1];
			}
		}
		return minmumBatchQuantity;
	}

	private checkMillAndShipToCombination() {
		this.stepState = this.getUpdatedStepState();
		const orderItems = this.stepState?.selectedOrderItems ?? [];
		for (const item of orderItems) {
			/* eslint-disable @typescript-eslint/no-base-to-string */
			const currentShipToDetails = item.shipTo && /^0+/.test(item.shipTo.toString())
				? this.allTransactionalShipToDetails?.find(shipto => shipto.shipTo
					=== item.shipTo?.toString().replace(/^0+/, ''))
				: this.allTransactionalShipToDetails?.find(shipto => shipto.shipTo === item.shipTo?.shipTo);
			const isValid = this.ecallOffService.validateMillAndShipToCombination(
				currentShipToDetails,
				item.mill,
			);
			if (!isValid) {
				this.snackBar.open(
					this.translationService.getTR('ecall.exct-date-error'),
					undefined,
					{ duration: 5000 }
				);
				return false;
			}
		}
		return true;
	}
}
